'use client';

import { ChangeEvent, FC, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { StandardDialog } from '@/components/common/StandardDialog';
import DetailField from '@/components/common/DetailField';
import { DebouncedTextField } from '@/components/common/DebouncedTextField';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { useBackendStore } from '@/stores/backendStore';
import { useSnackbar } from '@/hooks/useSnackbar';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { useDialogStore } from '@/stores/dialogStore';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import {
  CreateProjectActivityRequest,
  CreatePublicationRequest,
  UpdatePublicationRequest,
  ProjectActivityDto,
  ProjectDto,
  PublicationDto,
  PublicationTypeDto,
  VisibilityDto,
  ActivityTypeDto,
  ActivityCategoryDto,
} from '@/api/ApiClient';
import {
  parseImportedCitation,
  ImportedPublicationDraft,
  buildCitationFromFields,
} from '@/lib/publication/citationImport';
import { getPublicationTypeDisplay, PUBLICATION_TYPE_DISPLAY } from '@/lib/types/publicationTypes';
import { validateRequiredToSubmit } from '@/lib/utils/submitValidation';
import { log } from '@/lib/utils/logger';
import { buildPublicationSubmitValidationFields } from '@/lib/publication/publicationSubmitValidation';

type DialogResult = ProjectActivityDto | PublicationDto | null;

interface CreatePublicationDialogProps {
  open: boolean;
  project?: ProjectDto;
  publication?: PublicationDto;
  onClose: (result?: DialogResult) => void;
}

type Step = 'import' | 'form';

const SELECT_KEYS: OptionKey[] = [
  'projects',
  'publicationType',
  'scienceDrivers',
  'materials',
  'synthesisInstruments',
  'characterizationInstruments',
];

function getInitialDialogDraft(publication?: PublicationDto): ImportedPublicationDraft {
  if (!publication) {
    return {
      title: '',
      authors: '',
      journal: '',
      cite: '',
      doi: '',
      description: '',
    };
  }
  return {
    title: publication.title || '',
    authors: publication.authors || '',
    journal: publication.journal || '',
    cite: publication.cite || '',
    doi: publication.doi || '',
    description: publication.description || '',
    year: publication.year,
  };
}

function toPublicationRequest(
  draft: ImportedPublicationDraft,
  publicationType: PublicationTypeDto,
  selectedProjectIds: number[],
  scienceDriverIds: number[],
  materialIds: number[],
  synthesisInstrumentIds: string[],
  characterizationInstrumentIds: string[]
): CreatePublicationRequest {
  return {
    title: draft.title?.trim() ?? '',
    year: draft.year,
    type: publicationType,
    authors: draft.authors?.trim() ?? '',
    journal: draft.journal?.trim() ?? '',
    cite: draft.cite?.trim() ?? '',
    doi: draft.doi?.trim() ?? '',
    description: draft.description?.trim() ?? '',
    projectIds: selectedProjectIds,
    scienceDriverIds,
    materialIds,
    synthesisInstrumentIds,
    characterizationInstrumentIds,
  };
}

export const CreatePublicationDialog: FC<CreatePublicationDialogProps> = ({ open, project, publication, onClose }) => {
  const backend = useBackendStore((state) => state.getBackend());
  const { showSnackbar } = useSnackbar();
  const { showConfirmDialog } = useDialogStore();
  const { options, loading: optionsLoading } = useSelectOptions(SELECT_KEYS);
  const isProjectMode = Boolean(project?.id);
  const isEditMode = Boolean(publication?.id);

  const [step, setStep] = useState<Step>(isEditMode ? 'form' : 'import');
  const [importText, setImportText] = useState('');
  const [draft, setDraft] = useState<ImportedPublicationDraft>(getInitialDialogDraft(publication));
  const [citationLocked, setCitationLocked] = useState(!isEditMode && Boolean(publication?.cite));
  const [publicationType, setPublicationType] = useState<PublicationTypeDto>(
    publication?.type || PublicationTypeDto.InHouse
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasFormEdits, setHasFormEdits] = useState(false);
  const [selectedScienceDriverIds, setSelectedScienceDriverIds] = useState<number[]>(
    publication?.scienceDriverIds || []
  );
  const [selectedMaterialIds, setSelectedMaterialIds] = useState<number[]>(
    publication?.materials?.map((m) => m.id).filter((id): id is number => id !== undefined) || []
  );
  const [selectedSynthesisInstrumentIds, setSelectedSynthesisInstrumentIds] = useState<string[]>(
    publication?.synthesisInstruments?.map((i) => i.id).filter((id): id is string => Boolean(id)) || []
  );
  const [selectedCharacterizationInstrumentIds, setSelectedCharacterizationInstrumentIds] = useState<string[]>(
    publication?.characterizationInstruments?.map((i) => i.id).filter((id): id is string => Boolean(id)) || []
  );
  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>(
    publication?.projects?.map((p) => p.id).filter((id): id is number => id !== undefined) ||
      (isProjectMode ? [project!.id!] : [])
  );
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const scienceDrivers = options.scienceDrivers || [];

  const reset = () => {
    setStep(isEditMode ? 'form' : 'import');
    setImportText('');
    setDraft(getInitialDialogDraft(publication));
    setCitationLocked(false);
    setPublicationType(publication?.type || PublicationTypeDto.InHouse);
    setHasFormEdits(false);
    setSelectedScienceDriverIds(publication?.scienceDriverIds || []);
    setSelectedMaterialIds(
      publication?.materials?.map((m) => m.id).filter((id): id is number => id !== undefined) || []
    );
    setSelectedSynthesisInstrumentIds(
      publication?.synthesisInstruments?.map((i) => i.id).filter((id): id is string => Boolean(id)) || []
    );
    setSelectedCharacterizationInstrumentIds(
      publication?.characterizationInstruments?.map((i) => i.id).filter((id): id is string => Boolean(id)) || []
    );
    setSelectedProjectIds(
      publication?.projects?.map((p) => p.id).filter((id): id is number => id !== undefined) ||
        (isProjectMode ? [project!.id!] : [])
    );
  };

  const handleClose = (result?: DialogResult) => {
    reset();
    onClose(result);
  };

  const syncCitationIfLocked = (next: ImportedPublicationDraft) => {
    if (!citationLocked) return next;
    return {
      ...next,
      cite: buildCitationFromFields({
        title: next.title,
        authors: next.authors,
        journal: next.journal,
        year: next.year,
        doi: next.doi,
      }),
    };
  };

  const updateDraft = (patch: Partial<ImportedPublicationDraft>) => {
    setHasFormEdits(true);
    setDraft((prev) => syncCitationIfLocked({ ...prev, ...patch }));
  };

  const markFormEdited = () => {
    setHasFormEdits(true);
  };

  const runImport = async (raw: string) => {
    if (!raw.trim()) {
      showSnackbar('Nothing to import.', 'warning');
      return;
    }
    if (hasFormEdits) {
      const ok = await showConfirmDialog({
        title: 'Overwrite Imported Values?',
        message: 'Re-import will overwrite current form values. Continue?',
        confirmText: 'Overwrite',
        isDestructive: true,
      });
      if (!ok) return;
    }
    try {
      const parsed = parseImportedCitation(raw);
      setDraft(parsed);
      setCitationLocked(true);
      setHasFormEdits(false);
      setStep('form');
      showSnackbar('Citation imported.', 'success');
    } catch (error) {
      log.error('[CreatePublicationDialog] Import parse failed', error);
      showSnackbar('Could not parse citation content.', 'error');
      setStep('form');
    }
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      setImportText(text);
      void runImport(text);
    };
    reader.onerror = () => {
      showSnackbar('Failed to read file.', 'error');
    };
    reader.readAsText(file);
  };

  /**
   * Applies the same required-field semantics as details submit validation
   * so we fail fast client-side instead of surfacing backend validation errors.
   */
  const validateFormForSubmit = () =>
    validateRequiredToSubmit(
      buildPublicationSubmitValidationFields({
        title: draft.title,
        authors: draft.authors,
        journal: draft.journal,
        cite: draft.cite,
        doi: draft.doi,
        type: publicationType,
        year: draft.year,
        requireMainScienceDriver: true,
        selectedScienceDriverIds,
      })
    );

  const canSubmit = validateFormForSubmit().isValid;

  const submit = async () => {
    if (!backend) return;
    const validationResult = validateFormForSubmit();
    if (!validationResult.isValid) {
      showSnackbar(
        `Cannot ${isEditMode ? 'save' : 'create'} publication. Please complete the following fields: ${validationResult.errors.join(', ')}`,
        'warning',
        10000,
        'Validation Failed'
      );
      return;
    }
    setIsSubmitting(true);
    try {
      if (isEditMode && publication) {
        const payload: UpdatePublicationRequest = {
          id: publication.id,
          ...toPublicationRequest(
            draft,
            publicationType,
            selectedProjectIds,
            selectedScienceDriverIds,
            selectedMaterialIds,
            selectedSynthesisInstrumentIds,
            selectedCharacterizationInstrumentIds
          ),
        };
        const updated = await backend.publication.update(String(publication.id), payload);
        showSnackbar('Publication saved successfully.', 'success');
        handleClose(updated);
        return;
      }

      if (isProjectMode) {
        const createdPublication = await backend.publication.create(
          toPublicationRequest(
            draft,
            publicationType,
            selectedProjectIds,
            selectedScienceDriverIds,
            selectedMaterialIds,
            selectedSynthesisInstrumentIds,
            selectedCharacterizationInstrumentIds
          )
        );
        const payload: CreateProjectActivityRequest = {
          projectId: project!.id!,
          type: ActivityTypeDto.Publication,
          category: ActivityCategoryDto.Project,
          date: new Date(),
          visibility: VisibilityDto.OnPublication,
          publication: createdPublication,
          desc: createdPublication.title,
        };
        const activity = await backend.projectActivity.create(payload);
        showSnackbar('Publication created successfully.', 'success');
        handleClose(activity);
        return;
      }

      const created = await backend.publication.create(
        toPublicationRequest(
          draft,
          publicationType,
          selectedProjectIds,
          selectedScienceDriverIds,
          selectedMaterialIds,
          selectedSynthesisInstrumentIds,
          selectedCharacterizationInstrumentIds
        )
      );
      showSnackbar('Publication created successfully.', 'success');
      handleClose(created);
    } catch (error: any) {
      showSnackbar(getProblemDetail(error) ?? 'Failed to create publication.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <StandardDialog
      open={open}
      onClose={() => handleClose()}
      title={isEditMode ? 'Edit Publication' : isProjectMode ? 'Add Publication' : 'Create Publication'}
      submitText={
        step === 'import' && !isEditMode
          ? 'Next'
          : isEditMode
            ? 'Save Publication'
            : isProjectMode
              ? 'Create Publication Activity'
              : 'Create Publication'
      }
      onSubmit={step === 'import' && !isEditMode ? () => runImport(importText) : submit}
      submitDisabled={step === 'import' && !isEditMode ? false : !canSubmit}
      isLoading={isSubmitting}
      maxWidth="lg"
      secondaryActions={
        isEditMode ? undefined : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label="Import"
              color={step === 'import' ? 'primary' : 'default'}
              onClick={() => setStep('import')}
              variant={step === 'import' ? 'filled' : 'outlined'}
            />
            <Chip
              label="Form"
              color={step === 'form' ? 'primary' : 'default'}
              onClick={() => setStep('form')}
              variant={step === 'form' ? 'filled' : 'outlined'}
            />
          </Box>
        )
      }
    >
      {step === 'import' && !isEditMode ? (
        <Stack spacing={2}>
          <TextField
            label="Paste RIS/BibTeX/citation"
            multiline
            minRows={10}
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            placeholder="Paste citation text here..."
            fullWidth
          />
          <Divider />
          <Box>
            <input ref={fileInputRef} type="file" hidden onChange={onFileChange} accept=".ris,.bib,.txt" />
            <Button startIcon={<UploadFileIcon />} variant="outlined" onClick={() => fileInputRef.current?.click()}>
              Upload / Drop File
            </Button>
          </Box>
        </Stack>
      ) : optionsLoading ? (
        <Box sx={{ py: 2, color: 'text.secondary' }}>Loading publication options...</Box>
      ) : (
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <DetailField
              label="Year"
              value={draft.year || 'N/A'}
              isReadOnly={false}
              required
              width={{ width: 'calc(50% - 8px)' }}
            >
              <DebouncedTextField
                value={draft.year ? String(draft.year) : ''}
                onUpdate={(value) => updateDraft({ year: value ? Number(value) : undefined })}
                fullWidth
                size="small"
                type="number"
              />
            </DetailField>
            <DetailField
              label="Journal"
              value={draft.journal || 'N/A'}
              isReadOnly={false}
              required
              width={{ width: 'calc(50% - 8px)' }}
            >
              <DebouncedTextField
                value={draft.journal || ''}
                onUpdate={(value) => updateDraft({ journal: value })}
                fullWidth
                size="small"
              />
            </DetailField>
          </Box>
          <DetailField label="Title" value={draft.title || ''} isReadOnly={false} required width={{ width: '100%' }}>
            <RichTextEditor value={draft.title || ''} onChange={(value) => updateDraft({ title: value })} />
          </DetailField>
          <DetailField
            label="Authors"
            value={draft.authors || ''}
            isReadOnly={false}
            required
            width={{ width: '100%' }}
          >
            <RichTextEditor value={draft.authors || ''} onChange={(value) => updateDraft({ authors: value })} />
          </DetailField>
          <DetailField
            label="Citation"
            value={<span dangerouslySetInnerHTML={{ __html: draft.cite || '' }} />}
            isReadOnly={false}
            required
            width={{ width: '100%' }}
          >
            <Box sx={{ position: 'relative', width: '100%' }}>
              <Box sx={{ width: '100%' }}>
                {citationLocked ? (
                  <Box
                    sx={{
                      minHeight: '2.5em',
                      p: 1.5,
                      pr: 4,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                    }}
                  >
                    <span dangerouslySetInnerHTML={{ __html: draft.cite || '' }} />
                  </Box>
                ) : (
                  <RichTextEditor value={draft.cite || ''} onChange={(value) => updateDraft({ cite: value })} />
                )}
              </Box>
              <Tooltip
                title={
                  citationLocked
                    ? 'Autogenerated from imported citation. Unlock to edit manually.'
                    : 'Citation is editable'
                }
              >
                <IconButton
                  onClick={() => {
                    markFormEdited();
                    setCitationLocked((v) => !v);
                  }}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    p: 0.5,
                    bgcolor: 'background.paper',
                  }}
                >
                  {citationLocked ? <LockIcon /> : <LockOpenIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </DetailField>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <DetailField
              label="DOI"
              value={draft.doi || 'N/A'}
              isReadOnly={false}
              required
              width={{ width: 'calc(50% - 8px)' }}
            >
              <DebouncedTextField
                value={draft.doi || ''}
                onUpdate={(value) => updateDraft({ doi: value })}
                fullWidth
                size="small"
              />
            </DetailField>
            <DetailField
              label="Type"
              value={getPublicationTypeDisplay(publicationType)}
              isReadOnly={false}
              required
              width={{ width: 'calc(50% - 8px)' }}
            >
              <Select
                value={publicationType}
                onChange={(e) => {
                  markFormEdited();
                  setPublicationType(e.target.value as PublicationTypeDto);
                }}
                fullWidth
                size="small"
              >
                {options.publicationType?.length
                  ? options.publicationType.map((typeOption) => (
                      <MenuItem key={typeOption.key} value={typeOption.key}>
                        {typeOption.value}
                      </MenuItem>
                    ))
                  : Object.entries(PUBLICATION_TYPE_DISPLAY).map(([key, label]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ))}
              </Select>
            </DetailField>
          </Box>
          <DetailField label="Description" value={draft.description || ''} isReadOnly={false} width={{ width: '100%' }}>
            <RichTextEditor value={draft.description || ''} onChange={(value) => updateDraft({ description: value })} />
          </DetailField>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <DetailField
              label="Main Science Driver"
              value={
                scienceDrivers.find((driver) => driver.id === selectedScienceDriverIds[0])?.label ||
                scienceDrivers.find((driver) => driver.id === selectedScienceDriverIds[0])?.name ||
                'None'
              }
              isReadOnly={false}
              required
              width={{ width: 'calc(50% - 8px)' }}
            >
              <Autocomplete
                options={scienceDrivers}
                getOptionLabel={(option) => option.label || option.name || String(option.id || '')}
                value={scienceDrivers.find((driver) => driver.id === selectedScienceDriverIds[0]) || null}
                onChange={(_, newValue) => {
                  markFormEdited();
                  const id = newValue?.id;
                  const rest = selectedScienceDriverIds.filter((existing) => existing !== id && existing !== undefined);
                  setSelectedScienceDriverIds(id ? [id, ...rest] : rest);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" placeholder="Select main driver" />
                )}
              />
            </DetailField>
            <DetailField
              label="Science Drivers"
              value={selectedScienceDriverIds.length ? String(selectedScienceDriverIds.length) : 'None'}
              isReadOnly={false}
              width={{ width: 'calc(50% - 8px)' }}
            >
              <Autocomplete
                multiple
                options={scienceDrivers}
                getOptionLabel={(option) => option.label || option.name || String(option.id || '')}
                value={scienceDrivers.filter((driver) => selectedScienceDriverIds.includes(driver.id ?? -1))}
                onChange={(_, newValue) => {
                  markFormEdited();
                  setSelectedScienceDriverIds(
                    newValue.map((driver) => driver.id).filter((id): id is number => id !== undefined)
                  );
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.label || option.name || String(option.id || '')}
                    />
                  ))
                }
                renderInput={(params) => <TextField {...params} fullWidth size="small" placeholder="Select drivers" />}
              />
            </DetailField>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <DetailField
              label="Materials"
              value={selectedMaterialIds.length ? String(selectedMaterialIds.length) : 'None'}
              isReadOnly={false}
              width={{ width: 'calc(50% - 8px)' }}
            >
              <Autocomplete
                multiple
                options={options.materials || []}
                getOptionLabel={(option) => option.name || String(option.id || '')}
                value={options.materials?.filter((m) => selectedMaterialIds.includes(m.id ?? -1)) || []}
                onChange={(_, newValue) => {
                  markFormEdited();
                  setSelectedMaterialIds(
                    newValue.map((item) => item.id).filter((id): id is number => id !== undefined)
                  );
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option.id} label={option.name || option.id} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" placeholder="Select materials" />
                )}
              />
            </DetailField>
            <DetailField
              label="Associated Projects"
              value={selectedProjectIds.length ? selectedProjectIds.join(', ') : 'None'}
              isReadOnly={false}
              width={{ width: 'calc(50% - 8px)' }}
            >
              <Autocomplete
                multiple
                options={options.projects || []}
                getOptionLabel={(option) => option.projectLabel || String(option.id || '')}
                value={options.projects?.filter((p) => selectedProjectIds.includes(p.id ?? -1)) || []}
                onChange={(_, newValue) => {
                  markFormEdited();
                  setSelectedProjectIds(newValue.map((item) => item.id).filter((id): id is number => id !== undefined));
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option.id} label={option.projectLabel || option.id} />
                  ))
                }
                renderInput={(params) => <TextField {...params} fullWidth size="small" placeholder="Select projects" />}
              />
            </DetailField>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <DetailField
              label="Synthesis Instruments"
              value={selectedSynthesisInstrumentIds.length ? String(selectedSynthesisInstrumentIds.length) : 'None'}
              isReadOnly={false}
              width={{ width: 'calc(50% - 8px)' }}
            >
              <Autocomplete
                multiple
                options={options.synthesisInstruments || []}
                getOptionLabel={(option) => `${option.id} - ${option.name}`}
                value={
                  options.synthesisInstruments?.filter((item) =>
                    selectedSynthesisInstrumentIds.includes(item.id || '')
                  ) || []
                }
                onChange={(_, newValue) => {
                  markFormEdited();
                  setSelectedSynthesisInstrumentIds(
                    newValue.map((item) => item.id).filter((id): id is string => Boolean(id))
                  );
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option.id} label={`${option.id} - ${option.name}`} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" placeholder="Select synthesis instruments" />
                )}
              />
            </DetailField>
            <DetailField
              label="Characterization Instruments"
              value={
                selectedCharacterizationInstrumentIds.length
                  ? String(selectedCharacterizationInstrumentIds.length)
                  : 'None'
              }
              isReadOnly={false}
              width={{ width: 'calc(50% - 8px)' }}
            >
              <Autocomplete
                multiple
                options={options.characterizationInstruments || []}
                getOptionLabel={(option) => `${option.id} - ${option.name}`}
                value={
                  options.characterizationInstruments?.filter((item) =>
                    selectedCharacterizationInstrumentIds.includes(item.id || '')
                  ) || []
                }
                onChange={(_, newValue) => {
                  markFormEdited();
                  setSelectedCharacterizationInstrumentIds(
                    newValue.map((item) => item.id).filter((id): id is string => Boolean(id))
                  );
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip {...getTagProps({ index })} key={option.id} label={`${option.id} - ${option.name}`} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" placeholder="Select characterization instruments" />
                )}
              />
            </DetailField>
          </Box>
        </Stack>
      )}
    </StandardDialog>
  );
};
